// src/components/UpgradeBanner.js
import React, { useState } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { createUpgradeSession } from '../services/stripe.service';
import { getNextTier } from '../config/pricing';
import { Crown, X, Sparkles, Loader2 } from 'lucide-react';
import { toast } from './Toast';

const UpgradeBanner = () => {
  const { currentUser } = useAuth();
  const { subscriptionStatus, loading } = useSubscription();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('upgradeBannerDismissed') === '1');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('upgradeBannerDismissed', '1');
  };

  // Don't show if loading or dismissed
  if (loading || dismissed) {
    return null;
  }

  // 2026-05-30: 2-tier world — banner only shows for Free. Starter and
  // Enterprise no longer exist as user-facing tiers; any legacy
  // subscription rows still on plan_type='starter'/'enterprise' resolve
  // to Plus via LEGACY_TIER_MAP and won't see this banner.
  const tier = subscriptionStatus.currentTier || 'free';
  if (tier !== 'free') {
    return null;
  }

  // Get the next tier to suggest (only 'pro' / Plus in the 2-tier world)
  const nextTier = getNextTier(subscriptionStatus.currentTier || 'free');
  if (!nextTier) return null;

  // Don't show if no price IDs configured for next tier
  if (!nextTier.priceIds.month && !nextTier.priceIds.year) return null;

  const handleUpgrade = async () => {
    if (!currentUser) return;

    setCheckoutLoading(true);
    try {
      const result = await createUpgradeSession(
        nextTier.id,
        'month'
      );

      if (result.error) {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Dynamic messaging based on current tier.
  // 2026-05-30: only the 'free' case fires now — the early-return above
  // means we never reach this for paid tiers. The default branch stays
  // as a defensive fallback in case currentTier ever comes back as a
  // legacy value that resolved oddly.
  const getBannerMessage = () => {
    switch (subscriptionStatus.currentTier) {
      case 'free':
        return {
          main: 'Unlock everything',
          detail: `Unlimited inventory, sales, expenses, package tracking & more with ${nextTier.name}`,
        };
      default:
        return {
          main: `Upgrade to ${nextTier.name}`,
          detail: nextTier.description,
        };
    }
  };

  const message = getBannerMessage();

  return (
    <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-amber-500/20 px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <p className="text-sm text-zinc-700 dark:text-zinc-300">
            <span className="font-medium">{message.main}</span>
            <span className="hidden sm:inline text-zinc-500 dark:text-zinc-400 ml-1.5">
              — {message.detail}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleUpgrade}
            disabled={checkoutLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5
                     bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400
                     text-white font-medium text-sm rounded-lg transition-all
                     disabled:opacity-50 shadow-sm"
          >
            {checkoutLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Crown className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Upgrade to {nextTier.name}</span>
            <span className="sm:hidden">Upgrade</span>
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeBanner;
